'use client';

/**
 * ObeyaCostPlanOutputCard — the classical Cost Plan: one row per real
 * schedule requirement, decided or not.
 *
 * Deliberately minimal, by design (not an oversight): requirement /
 * product / room / qty / unit price / total price, nothing else. No
 * price ranges, no per-candidate quoted status, no inline qty stepper
 * — that comparison/editing chrome lives on Market Overview
 * (costplan-comparison-card.tsx) and Cost Summary
 * (costplan-lines-card.tsx), both left untouched. This card's whole
 * reason to exist is to be the one place that reads as "the final
 * output," the way a real bill of quantities does.
 *
 * TanStack Table (headless, already a listed dependency nobody used
 * yet) drives sort + column visibility ONLY — genuinely presentation-
 * only concerns, no different in kind from a hand-rolled sort.
 * Deliberately NOT using its grouping/aggregation row models: this
 * codebase already tried client-side pivot aggregation once and
 * reverted it on purpose (see CostPlanPivotParams' own docstring,
 * "no client-side aggregation... found doing real aggregation in
 * React") — grouping/value-range/collapse-to-winner logic stays
 * server-side, same discipline every sibling card in this file
 * follows.
 *
 * Undecided rows get their own section (header "Not yet decided · N"),
 * partitioned out of the sorted row model rather than interleaved —
 * they don't fit TanStack's per-column cell model either way (nothing
 * to show on five of six columns), so they're rendered outside the
 * normal flexRender path regardless of which column is sorted.
 *
 * `share` (each row's fraction of the plan's own decided total) and
 * `vendor_name`/`image_url`/`room_labels` all ride the same
 * `costplan_requirements` payload as the other figures — real BFF
 * math and already-loaded fields, nothing computed or re-derived here.
 * Currency rides the column header, not every cell (the per-cell
 * repetition was the single largest source of row-wrapping) — still
 * the real ISO code from data, never a localized symbol.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — content_key="obeya_costplan_output"
 * @see klyde_edge/klyde_intelligence/projects/handler.py — costplan_requirements
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-kpi-row-card.tsx — sibling, same "Total value" question
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { Fragment, useMemo, useState } from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel,
  createColumnHelper, flexRender, type SortingState, type SortingFn,
} from '@tanstack/react-table';

// Registers the 'decidedFirst' custom sorting fn name with TanStack's
// own types, so `sortingFn: 'decidedFirst'` on a column def type-checks
// — the standard TanStack v8 module-augmentation pattern for a custom
// named sortingFn, not a workaround specific to this card.
declare module '@tanstack/react-table' {
  interface SortingFns {
    decidedFirst: SortingFn<unknown>;
  }
}
import { ChevronUp, ChevronDown, ChevronsUpDown, Columns3 } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport, paintedRows } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface CostPlanRequirementRow {
  requirement_sid: string;
  requirement: string;
  decided: boolean;
  product_name?: string | null;
  vendor_name?: string | null;
  image_url?: string | null;
  room_labels?: string[];
  quantity?: number | null;
  unit_price?: number | null;
  total_price?: number | null;
  share?: number | null;
  // Odoo's own product_type on the winning pick ('service' vs a
  // physical good) — null when undecided. Drives the Products/Services
  // split below (client-side here, unlike the BFF-ordered split on
  // Market Overview/Cost Summary — this card's columns are live-
  // sortable, so a BFF-fixed row order would just get undone by
  // whichever column the viewer last clicked; filtering the already-
  // sorted row model, same as the existing decided/undecided partition,
  // keeps each section internally sorted by the active column).
  product_type?: string | null;
  // That section's real decided-row count and total_price sum —
  // stamped on the same row as section_label, by the BFF (same split
  // this card does client-side, see hasServiceSplit below).
  section_label?: string | null;
  section_count?: number | null;
  section_subtotal?: number | null;
}

interface CostPlanRequirementsData {
  rows: CostPlanRequirementRow[];
  requirement_count: number;
  decided_count: number;
  total_value: number;
  currency: string | null;
  labels?: Record<string, string>;
}

/** No currency suffix — currency rides the column header instead (see
 *  module docstring). Only the standalone footer total still carries
 *  its own currency, since it's read on its own, not under a labeled
 *  column. */
function moneyCell(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return amount.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function moneyTotal(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null) return '—';
  return `${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency ?? ''}`.trim();
}

function qty(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function RoomChips({ rooms }: { rooms: string[] | undefined }) {
  // Plain text, not chips — matches the vendor line underneath the
  // product (text-xs text-muted-foreground), not a boxed/filled badge.
  // A pill treatment here was the only chip in an otherwise plain-text
  // row, over-weighting the least important column against the actual
  // product/price data.
  if (!rooms || rooms.length === 0) return <span className={text.muted}>—</span>;
  const shown = rooms.slice(0, 2);
  const rest = rooms.slice(2);
  const label = rest.length > 0 ? `${shown.join(', ')} +${rest.length}` : shown.join(', ');
  return (
    // block, not inline: padding-left on an inline span only offsets the
    // first line — a wrapped second line falls back to the cell's own
    // edge, unindented. A block box applies the padding to every line.
    <span className="block pl-2 text-xs text-muted-foreground" title={rest.length > 0 ? rooms.join(', ') : undefined}>
      {label}
    </span>
  );
}

const columnHelper = createColumnHelper<CostPlanRequirementRow>();

export function ObeyaCostPlanOutputCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'requirements'));
  const lang = useLanguageStore((s) => s.lang);
  const { data: raw, isLoading } = useStrategyData<CostPlanRequirementsData>(
    strategy, { semantic_id: mcpId ?? '', ...(lang ? { lang } : {}) }, !!mcpId && !!strategy,
  );
  const d = raw?.data;
  const L = d?.labels ?? {};
  const currency = d?.currency;
  const data = useMemo(() => d?.rows ?? [], [d]);

  // Undecided rows have nothing to sort on any column — pin them to
  // the end regardless of which column/direction is active, rather
  // than letting null quantity/price sort unpredictably among them.
  const decidedFirst = useMemo(() => 'decidedFirst' as const, []);

  const columns = useMemo(() => [
    columnHelper.accessor('room_labels', {
      header: L.col_room ?? 'Room',
      cell: (ctx) => <RoomChips rooms={ctx.getValue()} />,
      sortingFn: (rowA, rowB) => {
        if (rowA.original.decided !== rowB.original.decided) return rowA.original.decided ? -1 : 1;
        const a = (rowA.original.room_labels ?? []).join(', ');
        const b = (rowB.original.room_labels ?? []).join(', ');
        return a.localeCompare(b);
      },
    }),
    columnHelper.accessor('product_name', {
      // Requirement rides as a caption above the product name (the
      // Composer's own "for: X" pattern) instead of its own column —
      // that freed-up width, plus the shorter per-row height from
      // dropping a column that used to wrap onto 2-3 lines on its own,
      // is most of this card's row-density win. Sorting now keys off
      // the product name directly; the requirement text isn't a
      // separately sortable axis any more (it wasn't a meaningful sort
      // key on its own either — every requirement is already unique).
      header: L.col_product ?? 'Product',
      cell: (ctx) => {
        const r = ctx.row.original;
        return (
          <span className="flex items-center gap-2 min-w-0">
            {r.image_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={r.image_url} alt="" className="w-11 h-11 rounded object-cover shrink-0 bg-muted" />
            ) : (
              <span className="w-11 h-11 rounded bg-muted shrink-0" />
            )}
            <span className="flex flex-col min-w-0">
              <span className={cn(text.muted, 'truncate')}>{r.requirement}</span>
              <span className="truncate">{ctx.getValue()}</span>
              {r.vendor_name && <span className={cn(text.muted, 'truncate')}>{r.vendor_name}</span>}
            </span>
          </span>
        );
      },
      sortingFn: decidedFirst,
    }),
    columnHelper.accessor('quantity', {
      header: L.col_qty ?? 'Qty',
      cell: (ctx) => <span className="tabular-nums">{qty(ctx.getValue())}</span>,
      meta: { align: 'right' as const },
      sortingFn: decidedFirst,
    }),
    columnHelper.accessor('unit_price', {
      // No per-column currency suffix any more — "Unit price (AUD)"
      // used to break onto 3 lines in a narrow column; the currency
      // now reads once, in the toolbar (see the render below), since
      // it's the same ISO code for every row on this plan.
      header: L.col_unit_price ?? 'Unit',
      cell: (ctx) => <span className={cn(text.muted, 'tabular-nums')}>{moneyCell(ctx.getValue())}</span>,
      meta: { align: 'right' as const },
      sortingFn: decidedFirst,
    }),
    columnHelper.accessor('total_price', {
      // The number buyers actually act on — semibold, full-foreground
      // weight, Unit price muted beside it (above) rather than the two
      // reading at the same visual weight.
      header: L.col_total_price ?? 'Total',
      cell: (ctx) => {
        const r = ctx.row.original;
        return (
          <span className="inline-flex flex-col items-end w-full">
            <span className="tabular-nums font-semibold text-[1.05em]">{moneyCell(ctx.getValue())}</span>
            {r.share != null && (
              <span className="w-12 h-0.5 bg-primary/40 rounded-full mt-0.5" style={{ width: `${Math.max(r.share, 0.04) * 48}px` }} />
            )}
          </span>
        );
      },
      meta: { align: 'right' as const },
      sortingFn: decidedFirst,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [L, decidedFirst]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortingFns: {
      decidedFirst: (rowA, rowB, columnId) => {
        if (rowA.original.decided !== rowB.original.decided) return rowA.original.decided ? -1 : 1;
        const a = rowA.getValue(columnId) as unknown;
        const b = rowB.getValue(columnId) as unknown;
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
      },
    },
  });

  // Partition into decided / undecided regardless of sort state — the
  // undecided section always renders as its own block with its own
  // header, never interleaved (see module docstring).
  const allRows = table.getRowModel().rows;
  const decidedRows = allRows.filter((r) => r.original.decided);
  const undecidedRows = allRows.filter((r) => !r.original.decided);
  const colCount = table.getVisibleLeafColumns().length;

  // Products-vs-Services, decided rows only (an undecided row has no
  // product_type to judge) — only a real split when the plan actually
  // has both, same "only when one exists" rule every other cost-plan
  // view follows; otherwise decidedRows renders as today's single block.
  const decidedServiceRows = decidedRows.filter((r) => r.original.product_type === 'service');
  const decidedProductRows = decidedRows.filter((r) => r.original.product_type !== 'service');
  const hasServiceSplit = decidedServiceRows.length > 0 && decidedProductRows.length > 0;

  // The BFF stamps section_count/section_subtotal on whichever row
  // carries section_label — same convention as every other cost-plan
  // view (see split_products_services). This card re-partitions client-
  // side (above), so after a re-sort that row can be anywhere in its
  // own filtered set; .find() locates it regardless of position rather
  // than assuming it's still first.
  const productsTotals = decidedProductRows.find((r) => r.original.section_count != null)?.original;
  const servicesTotals = decidedServiceRows.find((r) => r.original.section_count != null)?.original;

  const queryKey = ['strategy-data', strategy, { semantic_id: mcpId ?? '', ...(lang ? { lang } : {}) }] as const;

  useCardExport(useMemo(() => {
    const visibleCols = table.getVisibleLeafColumns();
    const cols = visibleCols.map((c) => c.id);
    const headers: Record<string, string> = {};
    visibleCols.forEach((c) => { headers[c.id] = String(c.columnDef.header ?? c.id); });
    const exportRows = table.getRowModel().rows.map((row) => {
      const r = row.original;
      const out: Record<string, string> = {};
      for (const c of visibleCols) {
        if (!r.decided) {
          out[c.id] = c.id === 'product_name' ? r.requirement : '';
          continue;
        }
        // product_name carries the requirement too now (it's the
        // caption above the name on screen) — "<requirement> — <product>"
        // keeps both facts in the one exported column rather than
        // dropping the requirement text now that it has no column of
        // its own.
        if (c.id === 'product_name') out[c.id] = r.product_name ? `${r.requirement} — ${r.product_name}` : r.requirement;
        else if (c.id === 'room_labels') out[c.id] = (r.room_labels ?? []).join(', ');
        else if (c.id === 'quantity') out[c.id] = qty(r.quantity);
        else if (c.id === 'unit_price') out[c.id] = moneyCell(r.unit_price);
        else if (c.id === 'total_price') out[c.id] = moneyCell(r.total_price);
      }
      return out;
    });
    return paintedRows({
      label: 'Cost Plan',
      columns: cols,
      headers,
      rows: exportRows,
      content_key: contentKey ?? 'obeya_costplan_output',
      status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
      query_key: mcpId && strategy ? queryKey : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey, d, data, sorting, mcpId, strategy, queryKey, L, currency]));

  if (!mcpId) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Select a cost plan
      </div>
    );
  }

  if (isLoading && !d) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        …
      </div>
    );
  }

  return (
    // bg-card: every sibling obeya card self-applies this on its root
    // element (project-info-card, snapshot-card, plan-position-rooms-
    // card); this one didn't, so the canvas's own bg-background (grey)
    // showed through the whole table instead of a white surface.
    <div className="flex flex-col h-full overflow-auto bg-card">
      <div className="flex items-center justify-end gap-2 px-1 py-1 relative">
        {/* Currency now reads once here instead of on every Unit/Total
            header — see the unit_price column's own comment. */}
        {currency && <span className={cn(text.eyebrow, 'mr-auto')}>{currency}</span>}
        <button
          type="button"
          onClick={() => setColumnMenuOpen((v) => !v)}
          aria-label="Toggle columns"
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <Columns3 className="w-3.5 h-3.5" />
        </button>
        {columnMenuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setColumnMenuOpen(false)} aria-hidden="true" />
            <div role="menu" className="absolute right-1 top-7 z-50 w-44 rounded-md border border-border bg-popover shadow-md py-1 text-sm">
              {table.getAllLeafColumns().map((c) => (
                <label key={c.id} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent cursor-pointer">
                  <input
                    type="checkbox"
                    checked={c.getIsVisible()}
                    onChange={c.getToggleVisibilityHandler()}
                    className="accent-primary"
                  />
                  <span className={text.body}>{String(c.columnDef.header)}</span>
                </label>
              ))}
            </div>
          </>
        )}
      </div>
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 bg-card z-10">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-border/50 text-left">
              {hg.headers.map((h) => {
                const align = (h.column.columnDef.meta as { align?: 'right' } | undefined)?.align;
                const sorted = h.column.getIsSorted();
                return (
                  <th
                    key={h.id}
                    onClick={h.column.getToggleSortingHandler()}
                    className={cn(
                      text.muted, 'group font-normal py-2 pr-3 cursor-pointer select-none hover:text-foreground',
                      align === 'right' && 'text-right',
                    )}
                  >
                    <span className={cn('inline-flex items-center gap-0.5', align === 'right' && 'flex-row-reverse')}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {sorted === 'asc' ? <ChevronUp className="w-3 h-3" />
                        : sorted === 'desc' ? <ChevronDown className="w-3 h-3" />
                        : <ChevronsUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40" />}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {(hasServiceSplit
            ? [
              { label: L.section_products ?? 'Products', rows: decidedProductRows, totals: productsTotals },
              { label: L.section_services ?? 'Services', rows: decidedServiceRows, totals: servicesTotals },
            ]
            : [{ label: null, rows: decidedRows, totals: undefined }]
          ).map((section) => (
            <Fragment key={section.label ?? 'decided'}>
              {section.label && (
                <tr className="bg-muted/40 border-t border-border">
                  <td colSpan={colCount} className={cn(text.muted, 'px-0 py-1.5 font-medium uppercase tracking-wide')}>
                    {section.label}
                  </td>
                </tr>
              )}
              {section.rows.map((row) => (
                <tr key={row.id} className="border-b border-border/20">
                  {row.getVisibleCells().map((cell) => {
                    const align = (cell.column.columnDef.meta as { align?: 'right' } | undefined)?.align;
                    return (
                      <td
                        key={cell.id}
                        className={cn(text.body, 'py-2 pr-3', align === 'right' && 'text-right')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </Fragment>
          ))}
          {undecidedRows.length > 0 && (
            <>
              <tr>
                <td colSpan={colCount} className={cn(text.muted, 'pt-4 pb-1 pr-3 font-medium')}>
                  {L.requirement_undecided ?? 'Not yet decided'} · {undecidedRows.length}
                </td>
              </tr>
              {undecidedRows.map((row) => (
                <tr key={row.id} className="border-b border-border/20">
                  <td className={cn(text.muted, 'py-1.5 pr-3')} colSpan={colCount}>
                    {row.original.requirement}
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t border-border">
            <td className={cn(text.muted, 'py-2 pr-3')} colSpan={Math.max(colCount - 1, 1)}>
              {d
                ? `${d.requirement_count} ${L.requirement_unit ?? 'requirements'} · ${d.decided_count} ${L.decided_unit ?? 'decided'}`
                : ''}
            </td>
            <td className={cn(text.value, 'py-2 text-right font-semibold tabular-nums')}>
              {d ? moneyTotal(d.total_value, d.currency) : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
